/**
 * CRM Dashboard
 * Huvudsida för CRM med moduler för Kundhantering och Ekonomi
 */

import { Link, useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { useTranslation } from '@/store/languageStore';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/store/authStore';
import {
  UserGroupIcon,
  BuildingOfficeIcon,
  FolderIcon,
  DocumentTextIcon,
  ChartBarSquareIcon,
  BanknotesIcon,
  DocumentChartBarIcon,
  ArrowTrendingUpIcon,
  CodeBracketIcon,
  ArrowLeftIcon,
  ClockIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { useAsyncData } from '@/hooks';
import type { ProjectDashboardStats } from '@/types/projects';

const CrmDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasFeatureAccess } = usePermissions();

  const isAdmin = user?.is_global_admin === true;
  const isCrmPartner =
    user?.is_crm_partner === true ||
    user?.is_crm_partner === 1 ||
    user?.is_crm_partner === '1';
  const canSee = (code: string) => isAdmin || hasFeatureAccess(code);

  // CRM-sektionerna
  const hasCrmAccess = isCrmPartner || canSee('CRM') || canSee('CRM_CUSTOMERS');
  const hasEconomyAccess = !isCrmPartner && canSee('CRM_ECONOMY');
  const hasProjectsAccess = !isCrmPartner && (canSee('CRM_PROJECTS') || canSee('CRM'));

  const { data: projectStats } = useAsyncData<ProjectDashboardStats>('/api/crm/projects/stats', {
    skip: !hasProjectsAccess,
  });

  return (
    <div className="p-4 bg-bgContainer rounded-lg h-full overflow-auto">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(ROUTES.CRM)}
          className="p-2 hover:bg-bgElevated rounded-lg transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
        </button>
        <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
          <UserGroupIcon className="w-6 h-6 text-primary" />
          {t('crm.title') || 'CRM'}
        </h1>
      </div>

      {hasProjectsAccess && projectStats && (
        <section className="mb-6 grid grid-cols-2 md:grid-cols-5 gap-3">
          <ProjectStat label={t('projects.stats.active')} value={projectStats.active} />
          <ProjectStat label={t('projects.stats.myTasks')} value={projectStats.my_tasks} />
          <ProjectStat label={t('projects.stats.overdue')} value={projectStats.overdue_tasks} alert />
          <ProjectStat label={t('projects.stats.hoursWeek')} value={`${projectStats.hours_this_week}h`} icon />
          <Link to={ROUTES.CRM_PROJECTS} className="p-3 bg-bgElevated rounded-lg border border-border hover:border-primary flex items-center gap-2">
            <FolderIcon className="w-5 h-5 text-primary" />
            <span className="text-sm font-medium">{t('crm.projects')}</span>
          </Link>
        </section>
      )}

      {/* CRM Modul */}
      {hasCrmAccess && (
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-textDefault mb-4 flex items-center gap-2">
            <UserGroupIcon className="w-6 h-6 text-bgPrimary" />
            {t('crm.crmModule') || 'Kundhantering'}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link
              to={ROUTES.CRM_CUSTOMERS}
              className="p-5 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <BuildingOfficeIcon className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-textDefault">
                    {t('crm.customers') || 'Kunder'}
                  </h3>
                  <p className="text-sm text-textSecondary">
                    {t('crm.customersDescription') || 'Hantera kundkort och kontakter'}
                  </p>
                </div>
              </div>
            </Link>

            {!isCrmPartner && hasProjectsAccess && (
            <Link
              to={ROUTES.CRM_PROJECTS}
              className="p-5 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <FolderIcon className="w-7 h-7 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-textDefault">
                    {t('crm.projects') || 'Projekt'}
                  </h3>
                  <p className="text-sm text-textSecondary">
                    {t('crm.projectsDescription') || 'Pågående och planerade projekt'}
                  </p>
                </div>
              </div>
            </Link>
            )}

            {!isCrmPartner && (
            <Link
              to={ROUTES.CRM_PROCUREMENTS}
              className="p-5 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                  <DocumentTextIcon className="w-7 h-7 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-textDefault">
                    {t('crm.procurements') || 'Upphandlingar'}
                  </h3>
                  <p className="text-sm text-textSecondary">
                    {t('crm.procurementsDescription') || 'LOU och anbudsprocesser'}
                  </p>
                </div>
              </div>
            </Link>
            )}

            {!isCrmPartner && (
            <Link
              to={ROUTES.CRM_PIPELINE}
              className="p-5 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                  <ChartBarSquareIcon className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-textDefault">
                    {t('crm.pipeline') || 'Säljpipeline'}
                  </h3>
                  <p className="text-sm text-textSecondary">
                    {t('crm.pipelineDescription') || 'Försäljningstratt och leads'}
                  </p>
                </div>
              </div>
            </Link>
            )}

            {!isCrmPartner && (
            <Link
              to={ROUTES.CRM_PRICELIST}
              className="p-5 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-rose-100 dark:bg-rose-900/30 rounded-lg">
                  <DocumentTextIcon className="w-7 h-7 text-rose-600 dark:text-rose-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-textDefault">
                    {t('crm.pricelist') || 'Prislista'}
                  </h3>
                  <p className="text-sm text-textSecondary">
                    {t('crm.pricelistDescription') || 'Tjänster, material och priser'}
                  </p>
                </div>
              </div>
            </Link>
            )}

            {!isCrmPartner && (
            <Link
              to={ROUTES.CRM_MODULE_CATALOG}
              className="p-5 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                  <Squares2X2Icon className="w-7 h-7 text-teal-600 dark:text-teal-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-textDefault">
                    {t('crm.moduleCatalog.title') || 'Modulkatalog'}
                  </h3>
                  <p className="text-sm text-textSecondary">
                    {t('crm.moduleCatalog.dashboardDescription') || 'Översikt över alla moduler och funktioner'}
                  </p>
                </div>
              </div>
            </Link>
            )}

            {/* SCB-testning visas bara för global admin / sysadmin */}
            {!isCrmPartner && isAdmin && (
              <Link
                to={ROUTES.CRM_SCB_TESTING}
                className="p-5 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                    <CodeBracketIcon className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-textDefault">
                      {t('crm.scbTesting') || 'SCB API Testning'}
                    </h3>
                    <p className="text-sm text-textSecondary">
                      {t('crm.scbTestingDescription') || 'Testa SCB API endpoints och visa resultat'}
                    </p>
                  </div>
                </div>
              </Link>
            )}
          </div>
        </section>
      )}

      {/* Ekonomi Modul */}
      {hasEconomyAccess && (
        <section>
          <h2 className="text-xl font-semibold text-textDefault mb-4 flex items-center gap-2">
            <BanknotesIcon className="w-6 h-6 text-bgPrimary" />
            {t('crm.economyModule') || 'Ekonomi'}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link
              to={ROUTES.CRM_ECONOMY_OVERVIEW}
              className="p-5 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <ArrowTrendingUpIcon className="w-7 h-7 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-textDefault">
                    {t('crm.economyOverview') || 'Ekonomisk översikt'}
                  </h3>
                  <p className="text-sm text-textSecondary">
                    {t('crm.economyOverviewDescription') || 'Rullande 12, trender och intäkter'}
                  </p>
                </div>
              </div>
            </Link>

            <Link
              to={ROUTES.CRM_INVOICES}
              className="p-5 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                  <DocumentChartBarIcon className="w-7 h-7 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-textDefault">
                    {t('crm.invoices') || 'Fakturaunderlag'}
                  </h3>
                  <p className="text-sm text-textSecondary">
                    {t('crm.invoicesDescription') || 'Generera och hantera fakturor'}
                  </p>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}
    </div>
  );
};

function ProjectStat({
  label, value, alert, icon,
}: { label: string; value: string | number; alert?: boolean; icon?: boolean }) {
  return (
    <div className={`p-3 bg-bgElevated rounded-lg border border-border ${alert ? 'border-red-300' : ''}`}>
      <div className="flex items-center gap-1">
        {icon && <ClockIcon className="w-4 h-4 text-textSecondary" />}
        <span className={`text-xl font-bold ${alert ? 'text-red-600' : 'text-textDefault'}`}>{value}</span>
      </div>
      <div className="text-xs text-textSecondary">{label}</div>
    </div>
  );
}

export default CrmDashboard;
